<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'

import { AppFooter, AppHeader, ToastContainer } from '@ficpod/ui-components'

import { useAuthStore } from './stores/auth'

const authStore = useAuthStore()
const route = useRoute()

const isGatherSection = computed(() => route.path.startsWith('/gather'))

const readerNavLinks = [
  { to: '/works', label: 'Browse' },
  { to: '/my-works', label: 'My Works', show: !!authStore.user?.is_author }
]

const gatherNavLinks = [
  { to: '/gather', label: 'Boards' },
  { to: '/gather/popular', label: 'Popular' },
  { to: '/gather/recent', label: 'Recent' }
]

const navLinks = computed(() => isGatherSection.value ? gatherNavLinks : readerNavLinks)

const footerSections = [
  {
    title: 'Reader',
    links: [
      { to: '/works', label: 'Browse Works' },
      { to: '/genres', label: 'Genres' },
      { to: '/authors', label: 'Authors' }
    ]
  },
  {
    title: 'Community',
    links: [
      { to: '/gather', label: 'Gather' },
      { to: '/guidelines', label: 'Guidelines' },
      { to: '/support', label: 'Support' }
    ]
  },
  {
    title: 'Legal',
    links: [
      { to: '/terms', label: 'Terms of Service' },
      { to: '/privacy', label: 'Privacy Policy' },
      { to: '/content-policy', label: 'Content Policy' }
    ]
  }
]

async function handleSignOut() {
  await authStore.signOut()
  window.location.href = '/'
}
</script>

<template>
  <div class="page">
    <ToastContainer />
    <AppHeader
      v-model:authStore="authStore"
      :brand-name="isGatherSection ? 'Gather' : 'FicPod'"
      :brand-to="isGatherSection ? '/gather' : '/'"
      :nav-links="navLinks"
      :show-admin="!isGatherSection"
      @sign-out="handleSignOut"
    >
      <template #user-actions v-if="isGatherSection && authStore.user">
        <router-link to="/gather/create" class="btn btn--outline-cream">
          New Post
        </router-link>
        <router-link to="/profile" class="flex items-center gap-2 header__nav-link">
          <div class="w-8 h-8 rounded-full overflow-hidden bg-burgundy-muted flex items-center justify-center">
            <img v-if="authStore.user.avatar_url" :src="authStore.user.avatar_url" :alt="authStore.user.display_name || authStore.user.username" class="w-full h-full object-cover" />
            <div v-else class="text-cream font-medium text-sm">
              {{ authStore.user.display_name?.[0] || authStore.user.username[0] }}
            </div>
          </div>
        </router-link>
      </template>
      <template #guest-actions v-if="isGatherSection">
        <router-link to="/login" class="btn btn--ghost">
          Sign In
        </router-link>
        <router-link to="/signup" class="btn btn--secondary">
          Sign Up
        </router-link>
      </template>
    </AppHeader>

    <main class="page__main">
      <router-view />
    </main>

    <AppFooter
      :brand-name="isGatherSection ? 'Gather' : 'FicPod'"
      :description="isGatherSection ? 'Discuss stories, connect with authors, and join the community.' : 'A platform for self-published authors and their readers.'"
      :sections="footerSections"
    />
  </div>
</template>
